// Iter 120 — Project Knowledge Records list page.
//
// Surfaces every persistent verified answer captured from /ask. Filter
// by project; click any row to open the detail page (read-only record
// of a moment in time with all source PDF deep-links intact).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookCheck, Calendar, ShieldCheck, ExternalLink, Loader2, Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

function ConfBadge({ value }) {
  const v = (value || "").toUpperCase();
  const map = {
    HIGH:   "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    MEDIUM: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    LOW:    "border-orange-500/40 text-orange-300 bg-orange-500/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${map[v] || "border-slate-500/40 text-slate-300 bg-slate-500/10"}`}>
      <ShieldCheck size={10} /> {v || "—"}
    </span>
  );
}

export default function KnowledgeRecordsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");  // Iter 124 P1 — chip filter


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/knowledge-records").then((r) => {
      if (cancelled) return;
      setItems(r.data || []); setErr(null);
    }).catch((e) => {
      if (cancelled) return;
      setErr(e?.response?.data?.detail || "Could not load records.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = items.filter((it) => {
    if (projectFilter && (it.project_id || "") !== projectFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (it.question || "").toLowerCase().includes(q)
        || (it.answer || "").toLowerCase().includes(q)
        || (it.title || "").toLowerCase().includes(q);
  });

  // Iter 124 — derive the project chips from the loaded records so the
  // user can scope to a single project without leaving the list.
  const projectOptions = Array.from(
    new Set(items.map((it) => it.project_id).filter(Boolean))
  );

  return (
    <div className="min-h-screen bg-[var(--fop-surface)] text-[var(--fop-text)]">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BookCheck size={18} className="text-[var(--fop-primary)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
              Project knowledge · Verified answers
            </span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Project Knowledge Records</h1>
          <p className="text-[var(--fop-text-3)] text-sm max-w-2xl">
            Every saved Ask answer lives here with full traceability. Open a record six months from now and the spec section, page citation, and source PDF will still resolve.
          </p>
        </div>

        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-4)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by question, answer or title…"
            data-testid="knowledge-filter"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-sm focus:outline-none focus:border-[var(--fop-primary)]"
          />
        </div>

        {/* Iter 124 P1 — Project filter chips. Auto-derived from the
            loaded records so PMs running multiple projects can scope. */}
        {projectOptions.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-6" data-testid="knowledge-project-chips">
            <button
              onClick={() => setProjectFilter("")}
              data-testid="knowledge-project-chip-all"
              className={`px-3 py-1 text-xs rounded-full border ${
                !projectFilter
                  ? "border-[var(--fop-primary)] text-[var(--fop-text)] bg-[var(--fop-primary)]/10"
                  : "border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              }`}
            >
              All projects
            </button>
            {projectOptions.map((pid) => (
              <button
                key={pid}
                onClick={() => setProjectFilter(pid)}
                data-testid={`knowledge-project-chip-${pid}`}
                className={`px-3 py-1 text-xs rounded-full border font-mono ${
                  projectFilter === pid
                    ? "border-[var(--fop-primary)] text-[var(--fop-text)] bg-[var(--fop-primary)]/10"
                    : "border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                }`}
              >
                {pid.slice(0, 14)}…
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-[var(--fop-text-3)] text-sm flex items-center gap-2" data-testid="knowledge-loading">
            <Loader2 size={14} className="animate-spin" /> Loading records…
          </div>
        ) : err ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{err}</div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--fop-border-2)] p-8 text-center" data-testid="knowledge-empty">
            <p className="text-[var(--fop-text-2)] mb-2">No project knowledge records yet.</p>
            <p className="text-xs text-[var(--fop-text-4)] mb-4">Use <Link to="/ask" className="text-[var(--fop-primary)] hover:underline">Ask</Link> to verify a question, then click Create Project Knowledge Record.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="knowledge-list">
            {visible.map((it) => (
              <Link
                key={it.id}
                to={`/knowledge/${it.id}`}
                data-testid={`knowledge-row-${it.id}`}
                className="block rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] hover:border-[var(--fop-primary)] transition-colors p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--fop-text)] mb-1 truncate" title={it.question}>
                      {it.question}
                    </div>
                    <div className="text-xs text-[var(--fop-text-3)] line-clamp-2">{it.answer}</div>
                  </div>
                  <ConfBadge value={it.confidence} />
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--fop-text-4)] font-mono">
                  <span>{it.citations?.length || 0} citation{(it.citations?.length || 0) !== 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>{it.sources?.length || 0} source{(it.sources?.length || 0) !== 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Calendar size={11} /> {it.created_at?.slice(0, 10)}</span>
                  <span>·</span>
                  <span>{it.created_by_name || "—"}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
